import type { ChangeEvent } from "react";
import { styled } from "styled-components";

import { useTranslate } from "common/utilities/useTranslate";
import { PermissionOption } from "domains/createShareSettings/components/PermissionOption";
import type { Invite } from "domains/createShareSettings/types";

type InvitationInputProps = { invitation?: Invite; isFirst: boolean; setInvitation: (invite: Invite) => void };

export const InvitationInput = ({ invitation, isFirst, setInvitation }: InvitationInputProps) => {
  const translate = useTranslate();

  const onChangeEmail = (event: ChangeEvent<HTMLInputElement>) =>
    setInvitation({ email: event.target.value, permission: invitation?.permission ?? "viewer" });

  const onChangePermission = (event: ChangeEvent<HTMLSelectElement>) => {
    if (invitation?.email === undefined) return;
    const value = event.target.value === "editor" ? "editor" : "viewer";
    setInvitation({ ...invitation, permission: value });
  };

  return (
    <Container>
      <Input
        autoCapitalize="off"
        autoComplete="email"
        autoCorrect="off"
        onChange={onChangeEmail}
        placeholder={translate(isFirst ? "app.create.enter_email" : "app.create.add_additional_email")}
        type="email"
        value={invitation?.email ?? ""}
      />
      <PermissionOption
        disabled={invitation?.email === undefined}
        onChange={onChangePermission}
        value={invitation?.permission ?? "viewer"}
      />
    </Container>
  );
};

const Container = styled.div`
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.inputText};
  border-radius: 20px;
  box-sizing: border-box;
  display: flex;
  margin: 24px 0 8px;
  transition:
    border-color 0.3s,
    color 0.3s;
  width: 100%;

  & select {
    margin-bottom: -1px;
    margin-right: -1px;
    margin-top: -1px;
  }

  &:focus-within,
  &:hover {
    border-color: ${({ theme }) => theme.colors.inputTextFocused};

    & select {
      border-color: ${({ theme }) => theme.colors.inputTextFocused};
    }
  }
`;

const Input = styled.input`
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.inputText};
  display: inline-block;
  flex-grow: 1;
  padding: 10px 16px;

  &:focus,
  &:hover {
    background-color: transparent;
    color: ${({ theme }) => theme.colors.inputTextFocused};
    outline: none;
  }
`;
